import React, { createElement, useEffect, useState, useMemo, useCallback } from 'react';
import { Table as OriginalTable, Form, Field, Dialog, Button, ResponsiveGrid } from '@alifd/next';
import { nanoid } from 'nanoid';
import { renderFormItem } from '../edit-from/render-form-block-component';
import ColumnCell from '@/components/table/pro-table/components/column-cell';
const EditTable = (props) => {
    const { children = [], __designMode, field, listName, formProps, getHandler, changingField, getSelectRow, editType } = props;
    const { columns, spacing, primaryKey = 'uid', status, emptyContent } = formProps;
    const { onChange: onTableFieldChange, value = [] } = field?.init(listName);
    const isPreview = status === 'readonly' || editType === 'view';
    const isDesign = __designMode === 'design';
    const [selectedRows, setSelectedRows] = useState([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const dataSource = useMemo(() => value?.map((item) => ({ ...item, uid: item.uid ?? nanoid() } || [])), [value]);
    const formField = Field.useField({
        parseName: true,
    });
    const reset = useCallback(() => {
        formField?.reset();
        setSelectedRowKeys([]);
        setSelectedRows([]);
    }, [formField]);
    useEffect(() => {
        getHandler({ reset });
    }, [formField, getHandler, reset]);
    const onRowChange = useCallback((selectedRowKeys, records) => {
        formField.reset();
        setSelectedRowKeys(selectedRowKeys);
        setSelectedRows(records);
        getSelectRow(records);
        formField.setValues(records[0]);
    }, [formField, getSelectRow]);
    const save = () => {
        formField.validate((errors, values) => {
            if (!errors) {
                const currentPrimaryKey = selectedRows?.[0]?.[primaryKey];
                if (currentPrimaryKey) {
                    const newData = dataSource.map((item) => {
                        if (item[primaryKey] === currentPrimaryKey) {
                            return values;
                        }
                        return item;
                    });
                    onTableFieldChange(newData);
                    resetSelectedRows();
                    formField.reset();
                }
            }
        });
    };
    const resetSelectedRows = () => {
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };
    const cancle = () => {
        resetSelectedRows();
        formField.reset();
    };
    const rowSelection = {
        mode: 'single',
        onChange: onRowChange,
        selectedRowKeys,
        getProps: () => {
            return {
                disabled: isDesign || isPreview,
            };
        },
    };
    const onDelete = React.useCallback((index) => {
        Dialog.confirm({
            title: '确认删除吗',
            closeMode: ['close', 'esc', 'mask'],
            onOk: () => {
                const newData = dataSource;
                newData.splice(index, 1);
                onTableFieldChange(newData);
                resetSelectedRows();
            },
        });
    }, [dataSource, onTableFieldChange]);
    const Operation = React.useCallback(() => {
        const index = dataSource?.findIndex((d) => d[primaryKey] === selectedRowKeys[0]);
        const onAdd = () => {
            const newData = dataSource || [];
            newData?.splice(index + 1, 0, { [primaryKey]: nanoid() });
            onTableFieldChange(newData);
        };
        if (isPreview) {
            return null;
        }
        return (createElement("span", null,
            createElement(Button, { type: "primary", onClick: onAdd, disabled: isDesign }, "\u65B0\u589E"),
            createElement(Button, { warning: true, style: { marginLeft: 4 }, onClick: () => onDelete(index), disabled: isDesign || selectedRowKeys.length === 0 }, "\u5220\u9664")));
    }, [dataSource, isDesign, onDelete, onTableFieldChange, primaryKey, selectedRowKeys, isPreview]);
    return (createElement("div", null,
        createElement("div", { style: { marginBottom: 4 } },
            createElement(Operation, null)),
        createElement(OriginalTable.StickyLock, { dataSource: dataSource, primaryKey: primaryKey, rowSelection: rowSelection, size: 'small', fixedHeader: true, isZebra: true, crossline: true, maxBodyHeight: 224 }, formatTableColumn(children)),
        ((isDesign || selectedRows.length > 0) && !isPreview) && (createElement(Form, { isPreview: isPreview, field: formField, useLabelForErrorMessage: true },
            createElement(ResponsiveGrid, { gap: spacing, columns: columns }, children ? (formatFormItems(children, { ...formProps, changingField, field })) : (createElement("div", { className: "empty-content" }, emptyContent))),
            isPreview ? null : (createElement("div", { style: { textAlign: 'center' } },
                createElement(Button.Group, null,
                    createElement(Button, { onClick: save, type: "primary", disabled: isDesign || selectedRows.length === 0 }, "\u4FEE\u6539"),
                    createElement(Button, { onClick: cancle, disabled: isDesign }, "\u53D6\u6D88"))))))));
};
export default EditTable;
const formatTableColumn = (columns) => {
    return columns
        ?.filter((col) => col?.isShowInColumn)
        ?.map((item) => {
        const { label, name, componentName, componentProps: { type, dict, format } } = item;
        const formatType = formatTypeOptions.find(item => item.componentName === componentName)?.type;
        const columnsProps = { title: label, dataIndex: name, width: 200 };
        return createElement(OriginalTable.Column, { key: columnsProps.dataIndex, ...columnsProps, cell: (value, rowIndex, record) => {
                const cellProps = {
                    value,
                    rowIndex,
                    record,
                    formatType,
                    formatOptions: format,
                    dict,
                    type,
                };
                return createElement(ColumnCell, { ...cellProps });
            } });
    });
};
const formatFormItems = (columns, globalProps) => {
    const { labelAlign, labelCol, wrapperCol, labelTextAlign, changingField, field } = globalProps;
    return columns.map((item) => {
        return (createElement(ResponsiveGrid.Cell, { colSpan: item.columnSpan }, renderFormItem(item, {
            labelAlign,
            labelCol,
            wrapperCol,
            labelTextAlign,
            changingField,
            field,
        })));
    });
};
export const formatTypeOptions = [
    { value: 'text', title: '文本', componentName: 'CbayelFormInput', type: 'string' },
    { value: 'number', title: '数字', componentName: 'CbayelFormNumberInput', type: 'number' },
    { value: 'money', title: '金额', componentName: 'CbayelFormNumberInput', type: 'money' },
    { value: 'date', title: '日期', componentName: 'CbayelFormDatePicker', type: 'date' },
    { value: 'dict', title: '字典', componentName: 'CbayelFormDictSelect', type: 'dict' },
    {
        value: 'uniqueCode',
        title: '唯一标识',
        componentName: 'CbayelFormUniqueCodeSelect',
        type: 'uniqueCode',
    },
    { value: 'percent', title: '百分比', componentName: 'CbayelFormNumberInput', type: 'rate' },
    { value: 'boolean', title: '布尔值', componentName: 'CbayelFormInput', type: 'boolean' },
    { value: 'progress', title: '进度条', componentName: 'CbayelFormInput', type: 'string' },
    { value: 'link', title: '链接', componentName: 'CbayelFormInput', type: 'string' },
    { value: 'tag', title: '标签', componentName: 'CbayelFormInput', type: 'string' },
    { value: 'api', title: '查询', componentName: 'CbayelFormApiSelect', type: 'api' },
];
